import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Briefcase, FileText, Receipt, LogOut, CheckCircle, Clock,
  XCircle, Download, Eye, MapPin, DollarSign, Calendar
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400' },
  draft: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400' },
  sent: { label: 'Sent', color: 'bg-blue-500/20 text-blue-400' },
  accepted: { label: 'Accepted', color: 'bg-green-500/20 text-green-400' },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400' },
  paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400' },
  overdue: { label: 'Overdue', color: 'bg-red-500/20 text-red-400' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ClientPortalPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('jobs');

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (token) {
      fetchClientData(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchClientData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, jobsRes, quotesRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/api/client-portal/me`, { headers }),
        axios.get(`${API}/api/client-portal/jobs`, { headers }),
        axios.get(`${API}/api/client-portal/quotes`, { headers }),
        axios.get(`${API}/api/client-portal/invoices`, { headers }),
      ]);
      setClient(meRes.data);
      setJobs(jobsRes.data);
      setQuotes(quotesRes.data);
      setInvoices(invoicesRes.data);
      setIsLoggedIn(true);
    } catch (error) {
      localStorage.removeItem('client_token');
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/client-portal/login`, { email, password });
      localStorage.setItem('client_token', response.data.token);
      toast.success('Welcome back!');
      fetchClientData(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/client-portal/register`, { email, password, name, phone });
      localStorage.setItem('client_token', response.data.token);
      toast.success('Account created successfully!');
      fetchClientData(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    setIsLoggedIn(false);
    setClient(null);
    toast.success('Logged out');
  };

  const handleApproveQuote = async (quoteId, token) => {
    try {
      await axios.post(`${API}/api/client/quote/${token}/approve`);
      toast.success('Quote approved!');
      fetchClientData(localStorage.getItem('client_token'));
    } catch (error) {
      toast.error('Failed to approve quote');
    }
  };

  const handleDownloadPdf = async (quoteId, token, type = 'quote') => {
    try {
      const url = type === 'quote' 
        ? `${API}/api/client/quote/${token}/pdf`
        : `${API}/api/invoices/${quoteId}/pdf`;
      const response = await axios.get(url, { 
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('client_token')}` }
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${type}-${quoteId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border">
          <CardHeader className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_ezclaimwin/artifacts/aasxwc8a_ChatGPT_Image_Jan_4__2026__09_48_44_AM-removebg-preview.png" 
              alt="ConstructOne" 
              className="h-20 mx-auto mb-4"
            />
            <CardTitle className="text-2xl">{isRegistering ? 'Create Account' : 'Client Portal'}</CardTitle>
            <p className="text-muted-foreground">Access your projects, quotes, and invoices</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
              {isRegistering && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} required className="bg-background" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-background" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-background" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-background" />
              </div>
              <Button type="submit" className="w-full">{isRegistering ? 'Create Account' : 'Sign In'}</Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => setIsRegistering(!isRegistering)}>
                {isRegistering ? 'Already have an account? Sign In' : "Don't have an account? Register"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_ezclaimwin/artifacts/hptkd9bx_ChatGPT%20Image%20Dec%2027%2C%202025%2C%2001_26_00%20AM.png" 
              alt="ConstructOne" 
              className="h-10"
            />
            <div>
              <h1 className="font-semibold">Client Portal</h1>
              <p className="text-sm text-muted-foreground">Welcome, {client?.name}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout}><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <Briefcase className="h-10 w-10 text-primary opacity-50" />
              <div>
                <p className="text-2xl font-bold">{jobs.length}</p>
                <p className="text-sm text-muted-foreground">Active Projects</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <FileText className="h-10 w-10 text-blue-400 opacity-50" />
              <div>
                <p className="text-2xl font-bold">{quotes.filter(q => q.status === 'sent').length}</p>
                <p className="text-sm text-muted-foreground">Pending Quotes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex items-center gap-4">
              <Receipt className="h-10 w-10 text-yellow-400 opacity-50" />
              <div>
                <p className="text-2xl font-bold">{invoices.filter(i => i.status !== 'paid').length}</p>
                <p className="text-sm text-muted-foreground">Outstanding Invoices</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-secondary/50 mb-6">
            <TabsTrigger value="jobs">My Projects</TabsTrigger>
            <TabsTrigger value="quotes">Quotes</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            {jobs.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No projects found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {jobs.map(job => (
                  <Card key={job.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{job.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.address}</span>
                            {job.start_date && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(job.start_date)}</span>}
                          </div>
                          {job.description && <p className="text-sm text-muted-foreground mt-2">{job.description}</p>}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[job.status]?.color}`}>
                          {statusConfig[job.status]?.label}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-4 text-sm">
                        <span className="text-muted-foreground">{job.quote_count || 0} quotes</span>
                        <span className="text-muted-foreground">{job.invoice_count || 0} invoices</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            {quotes.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No quotes found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {quotes.map(quote => (
                  <Card key={quote.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{quote.quote_number}</h3>
                          <p className="text-sm text-muted-foreground">{quote.job?.title} - {quote.job?.address}</p>
                          <p className="text-lg font-bold mt-2">${quote.total?.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[quote.status]?.color}`}>
                            {statusConfig[quote.status]?.label}
                          </span>
                          <p className="text-xs text-muted-foreground mt-2">Valid until: {formatDate(quote.valid_until)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(quote.id, quote.client_token, 'quote')}>
                          <Download className="h-4 w-4 mr-1" /> Download PDF
                        </Button>
                        {quote.status === 'sent' && (
                          <Button size="sm" onClick={() => handleApproveQuote(quote.id, quote.client_token)}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            {invoices.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No invoices found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {invoices.map(inv => (
                  <Card key={inv.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{inv.invoice_number}</h3>
                          <p className="text-sm text-muted-foreground">{inv.job?.title}</p>
                          <p className="text-lg font-bold mt-2">${inv.total?.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[inv.status]?.color}`}>
                            {statusConfig[inv.status]?.label}
                          </span>
                          <p className="text-xs text-muted-foreground mt-2">Due: {formatDate(inv.due_date)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ClientPortalPage;
